/**
 * Map arbitrary spreadsheet column indices onto DDX ingest canonical fields.
 * @see DDX_INGEST_DEFAULT_COLUMN_KEYS in ddxIngestTypes.js
 */

import { cleanIngestTextField } from './ddxIngestClean.js';
import { DDX_INGEST_DEFAULT_COLUMN_KEYS } from './ddxIngestTypes.js';

/** @type {number} */
export const DDX_INGEST_SOURCE_BY_TARGET_LEN = DDX_INGEST_DEFAULT_COLUMN_KEYS.length;

/**
 * Legacy layout: source column i maps to target i (seven columns).
 * @returns {(number|null)[]}
 */
export function buildIdentitySourceByTarget() {
  return [0, 1, 2, 3, 4, 5, 6];
}

/**
 * @param {unknown} value
 * @returns {value is number|null}
 */
function isValidSourceIndexEntry(value) {
  if (value === null) return true;
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

/**
 * Structural validation for `sourceByTarget` (length, entry types, optional max column bound).
 *
 * @param {unknown} sourceByTarget
 * @param {number} [maxSourceCols] - reject if any index is >= this (e.g. max width seen in sample rows)
 * @returns {boolean}
 */
export function validateSourceByTarget(sourceByTarget, maxSourceCols) {
  if (!Array.isArray(sourceByTarget) || sourceByTarget.length !== DDX_INGEST_SOURCE_BY_TARGET_LEN) {
    return false;
  }
  for (let i = 0; i < sourceByTarget.length; i += 1) {
    const e = sourceByTarget[i];
    if (!isValidSourceIndexEntry(e)) return false;
    if (e != null && maxSourceCols != null && e >= maxSourceCols) return false;
  }
  return true;
}

/**
 * Normalize client/legacy payloads into a validated `sourceByTarget` or return null.
 *
 * @param {unknown} columnMap - `{ sourceByTarget: (number|null)[] }` or undefined
 * @param {number} [maxSourceCols]
 * @returns {(number|null)[]|null}
 */
export function normalizeColumnMapSourceByTarget(columnMap, maxSourceCols) {
  if (columnMap == null || typeof columnMap !== 'object') return null;
  const raw = /** @type {{ sourceByTarget?: unknown }} */ (columnMap).sourceByTarget;
  if (!validateSourceByTarget(raw, maxSourceCols)) return null;
  return /** @type {(number|null)[]} */ (raw.slice());
}

/**
 * Extract title (trim only) and cleaned non-title fields from one parsed row.
 *
 * @param {string[]} row
 * @param {(number|null)[]} sourceByTarget - length 7, aligned with DDX_INGEST_DEFAULT_COLUMN_KEYS
 * @returns {{ title: string, cleanedFields: Record<string, string> }}
 */
export function applyDdxIngestColumnMap(row, sourceByTarget) {
  const keys = DDX_INGEST_DEFAULT_COLUMN_KEYS;
  if (!Array.isArray(row) || !Array.isArray(sourceByTarget) || sourceByTarget.length !== keys.length) {
    return { title: '', cleanedFields: {} };
  }

  const titleIdx = sourceByTarget[0];
  let title = '';
  if (titleIdx != null && row[titleIdx] != null) {
    title = String(row[titleIdx]).trim();
  }

  /** @type {Record<string, string>} */
  const cleanedFields = {};
  for (let t = 1; t < keys.length; t += 1) {
    const key = keys[t];
    const si = sourceByTarget[t];
    const raw = si != null && row[si] != null ? String(row[si]) : '';
    cleanedFields[key] = cleanIngestTextField(raw);
  }
  return { title, cleanedFields };
}

/**
 * Heuristic for common 6-column EM-style sheets (title + 5 blobs, no questionCount column).
 * Maps: title←0, questionCount omitted, triage←1, differential←2, emergency←3, adultDdx←4, reviewer←5.
 *
 * @param {number} maxSourceCols - must be >= 6 to return this map; else null
 * @returns {(number|null)[]|null}
 */
export function suggestSourceByTargetSixContentColumns(maxSourceCols) {
  if (maxSourceCols < 6) return null;
  return [0, null, 1, 2, 3, 4, 5];
}
